// qs-useDrawingOperations.ts
import { brushRegistry } from '@/brushes/qs-brushes-index'
import type { BrushContext } from '@/brushes/qs-brushes-types'
import { emitDrawingCompleted } from '@/composables/qs-useEventBus'
import { useHistory } from '@/composables/qs-useHistory'
import { useWebWorkers } from '@/composables/qs-useWebWorkers'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useToolStore } from '@/stores/qs-store-tools'
import { validateCurrentLayerOperation } from '@/utils/qs-util-layerLock'
import type { KonvaEvent, KonvaLayer, KonvaLine, KonvaNode, KonvaStage } from '@/types/qs-type-konva'
import type { DrawingPath, SmoothingMode } from '@/types/qs-types-index'
import Konva from 'konva'
import { ref } from 'vue'

export function useDrawingOperations() {
  const canvasStore = useCanvasStore()
  const layerStore = useLayerStore()
  const toolStore = useToolStore()
  const history = useHistory()
  const webWorkers = useWebWorkers()
  const isEndingDrawing = ref(false)

  const ensureValidDrawingLayer = (): boolean => {
    const validation = validateCurrentLayerOperation('draw')
    if (!validation.canProceed) {
      const unlockedLayers = layerStore.layers.filter(l => !l.locked)
      if (unlockedLayers.length > 0) {
        const success = layerStore.setCurrentLayer(unlockedLayers[0].id)
        if (success) {
          return true
        }
      }
      return false
    }
    return true
  }

  const getCanvasPosition = (e: KonvaEvent | MouseEvent | TouchEvent, stage: KonvaStage): { x: number; y: number } | null => {
    const container = stage.container()
    if (!container) return null

    const rect = container.getBoundingClientRect()
    const scale = canvasStore.scale
    const position = canvasStore.position

    let clientX: number, clientY: number

    if ('touches' in e && e.touches && e.touches.length > 0) {
      clientX = e.touches[0]?.clientX || 0
      clientY = e.touches[0]?.clientY || 0
    } else if ('clientX' in e) {
      clientX = e.clientX || 0
      clientY = e.clientY || 0
    } else {
      clientX = 0
      clientY = 0
    }

    const baseX = (clientX - rect.left) / scale - position.x / scale
    const baseY = (clientY - rect.top) / scale - position.y / scale

    const currentLayer = layerStore.currentLayer
    const layerOffsetX = currentLayer?.position?.x || 0
    const layerOffsetY = currentLayer?.position?.y || 0

    return {
      x: baseX - layerOffsetX,
      y: baseY - layerOffsetY
    }
  }

  const createBrushContext = (position: { x: number; y: number }, layerId: string, konvaLayer: KonvaLayer): BrushContext => {
    return {
      position,
      size: toolStore.currentTool.size,
      color: toolStore.currentTool.color,
      opacity: toolStore.currentTool.opacity,
      layerId,
      konvaLayer,
      smoothingMode: toolStore.currentTool.smoothingMode || 'curved'
    }
  }

  const createFallbackLine = (position: { x: number; y: number }): KonvaLine => {
    return new Konva.Line({
      points: [position.x, position.y],
      stroke: toolStore.currentTool.color,
      strokeWidth: toolStore.currentTool.size,
      opacity: toolStore.currentTool.opacity,
      lineCap: 'round',
      lineJoin: 'round',
      listening: false
    })
  }

  const createPathData = (path: DrawingPath) => ({
    id: path.id,
    points: path.points,
    smoothedPoints: path.smoothedPoints,
    smoothingMode: path.smoothingMode,
    stroke: path.stroke || '#000000',
    strokeWidth: path.strokeWidth || 1,
    lineCap: path.lineCap || 'round',
    lineJoin: path.lineJoin || 'round',
    dash: path.dash,
    opacity: path.opacity || 1,
    brushType: path.brushType || 'round',
    visualData: path.visualData,
    isThickToThin: path.isThickToThin,
    isThinToThick: path.isThinToThick,
    segmentCount: path.segmentCount
  })

  const calculateSmoothedPoints = async (originalPoints: number[], smoothingMode: SmoothingMode): Promise<number[]> => {
    if (originalPoints.length < 4) {
      return originalPoints
    }

    if (smoothingMode === 'straight') {
      const startX = originalPoints[0]
      const startY = originalPoints[1]
      const endX = originalPoints[originalPoints.length - 2]
      const endY = originalPoints[originalPoints.length - 1]
      return [startX, startY, endX, endY]
    } else if (smoothingMode === 'light') {
      if (originalPoints.length >= 6) {
        try {
          const smoothed = await webWorkers.smoothPathMovingAverage(originalPoints, 5)
          return smoothed
        } catch (error) {
          return originalPoints
        }
      }
    }
    
    return originalPoints
  }

  const startDrawing = (e: KonvaEvent | MouseEvent | TouchEvent, stage: KonvaStage): void => {
    if (!ensureValidDrawingLayer()) {
      return
    }

    canvasStore.setDrawing(true)
    const pos = getCanvasPosition(e, stage)

    if (!pos) return

    const activeLayer = layerStore.currentLayer
    if (!activeLayer) {
      return
    }

    const newPath: DrawingPath = {
      id: `path-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      points: [pos.x, pos.y],
      stroke: toolStore.currentTool.color,
      strokeWidth: toolStore.currentTool.size,
      lineCap: 'round' as const,
      lineJoin: 'round' as const,
      opacity: toolStore.currentTool.opacity,
      timestamp: Date.now(),
      brushType: toolStore.currentTool.brushType,
      layerId: activeLayer.id,
      dash: [],
      isTapered: false,
      isThickToThin: false,
      isThinToThick: false,
      segmentCount: 0,
      visualData: { segments: [], dots: [] },
      isSpray: false,
      dotCount: 0
    }

    canvasStore.setCurrentPath(newPath)

    const brushType = toolStore.currentTool.brushType || 'round'
    const layer = stage.findOne(`#layer-${activeLayer.id}`) as KonvaLayer

    const needsLivePreview = ['dashed', 'spray'].includes(brushType)
    const needsFastDrawing = ['bow-tie', 'pointed', 'thick-to-thin', 'thin-to-thick'].includes(brushType)

    if (needsLivePreview && layer) {
      const brush = brushRegistry.getBrushByType(brushType)
      if (brush) {
        const context = createBrushContext(pos, activeLayer.id, layer)
        const konvaLine = brush.createBrushLine(context)
        layer.add(konvaLine)
        stage.batchDraw()
        canvasStore.setBrushLines([konvaLine])
      } else {
        const konvaLine = createFallbackLine(pos)
        layer.add(konvaLine)
        stage.batchDraw()
        canvasStore.setBrushLines([konvaLine])
      }
    } else if ((needsFastDrawing || layer) && layer) {
      const konvaLine = createFallbackLine(pos)
      layer.add(konvaLine)
      stage.batchDraw()
      canvasStore.setBrushLines([konvaLine])
    }
  }

  const continueDrawing = (pos: { x: number; y: number }, stage: KonvaStage): void => {
    const currentPath = canvasStore.currentPath
    const currentLayer = layerStore.currentLayer

    if (!currentPath || !currentLayer) {
      return
    }

    currentPath.points.push(pos.x, pos.y)

    const brushType = toolStore.currentTool.brushType || 'round'
    const needsLivePreview = ['dashed', 'spray'].includes(brushType)
    const existingLines = canvasStore.brushLines

    if (needsLivePreview && existingLines.length > 0) {
      const brush = brushRegistry.getBrushByType(brushType)
      if (brush) {
        const line = existingLines[0]
        if (line) {
          const context = createBrushContext(pos, currentLayer.id, stage.findOne(`#layer-${currentLayer.id}`) as KonvaLayer)
          brush.continueDrawing(line, context)
          stage.batchDraw()
        }
      } else {
        const line = existingLines[0]
        if (line && typeof line.points === 'function') {
          const currentPoints = line.points()
          currentPoints.push(pos.x, pos.y)
          line.points(currentPoints)
          stage.batchDraw()
        }
      }
    } else if (existingLines.length > 0) {
      const line = existingLines[0]
      if (line && typeof line.points === 'function') {
        const currentPoints = line.points()
        currentPoints.push(pos.x, pos.y)
        line.points(currentPoints)
        stage.batchDraw()
      }
    }
  }

  const replaceSimpleLineWithBrushEffect = async (
    currentPath: DrawingPath, 
    activeLayer: { id: string }, 
    stage: KonvaStage, 
    visualSegments: Array<{ points: number[]; strokeWidth: number; opacity: number }>
  ): Promise<void> => {
    const layer = stage.findOne(`#layer-${activeLayer.id}`) as KonvaLayer
    if (!layer) return

    const existingLines = canvasStore.brushLines
    if (existingLines.length > 0) {
      existingLines[0].destroy()
      canvasStore.setBrushLines([])
    }

    if (visualSegments.length > 0) {
      const brushLines: KonvaLine[] = []
      
      for (const segment of visualSegments) {
        const konvaLine = new Konva.Line({
          points: segment.points,
          stroke: currentPath.stroke,
          strokeWidth: segment.strokeWidth,
          opacity: segment.opacity,
          lineCap: 'round',
          lineJoin: 'round',
          listening: false
        })
        
        layer.add(konvaLine)
        brushLines.push(konvaLine)
      }
      
      canvasStore.setBrushLines(brushLines)
      stage.batchDraw()
    }
  }

  const finishBrushDrawing = async (currentPath: DrawingPath, activeLayer: { id: string }, stage: KonvaStage): Promise<void> => {
    const smoothingMode = toolStore.currentTool.smoothingMode || 'curved'

    let smoothedPoints: number[]
    if (currentPath.smoothedPoints && currentPath.smoothingMode === smoothingMode) {
      smoothedPoints = currentPath.smoothedPoints
    } else {
      smoothedPoints = smoothingMode !== 'curved' 
        ? await calculateSmoothedPoints(currentPath.points, smoothingMode)
        : currentPath.points
      currentPath.smoothedPoints = smoothedPoints
      currentPath.smoothingMode = smoothingMode
    }

    const brushType = toolStore.currentTool.brushType || 'round'
    const brush = brushRegistry.getBrushByType(brushType)

    if (!brush) {
      return
    }

    const context = createBrushContext({ x: 0, y: 0 }, activeLayer.id, stage.findOne(`#layer-${activeLayer.id}`) as KonvaLayer)
    const existingLines = canvasStore.brushLines

    if (existingLines.length === 0) return

    const line = existingLines[0]
    if (!line) return

    let validSmoothedPoints: number[]
    
    if (smoothingMode === 'straight') {
      if (smoothedPoints.length === 4 && smoothedPoints.every(p => isFinite(p))) {
        validSmoothedPoints = smoothedPoints
      } else {
        validSmoothedPoints = currentPath.points
      }
    } else {
      validSmoothedPoints = smoothedPoints.filter((p, idx) => {
        if (idx % 2 === 0) {
          return isFinite(p) && isFinite(smoothedPoints[idx + 1])
        }
        return true
      })
      
      if (validSmoothedPoints.length < 4) {
        validSmoothedPoints = currentPath.points
      }
    }

    const isTaperingBrush = ['pointed', 'thick-to-thin', 'thin-to-thick', 'bow-tie'].includes(brushType)
    
    if (isTaperingBrush) {
      line.points(validSmoothedPoints)
      line.lineJoin('round')
      line.lineCap('round')
      line.tension(0)
      line.closed(false)
      
      const lineLayer = line.getLayer()
      if (lineLayer) {
        lineLayer.batchDraw()
      }
      stage.batchDraw()
    } else {
      if (line && typeof line.points === 'function') {
        line.points(validSmoothedPoints)
        line.lineJoin('round')
        line.lineCap('round')
        line.tension(0)
        line.closed(false)
        
        const lineLayer = line.getLayer()
        if (lineLayer) {
          lineLayer.batchDraw()
        }
        stage.batchDraw()
      }
    }

    canvasStore.setBrushLines([])

    const result = await brush.finishDrawing(line, context)

    if (result.pathData) {
      Object.assign(currentPath, {
        points: currentPath.points,
        smoothedPoints: smoothedPoints,
        smoothingMode: smoothingMode,
        stroke: result.pathData.stroke,
        strokeWidth: result.pathData.strokeWidth,
        lineCap: result.pathData.lineCap,
        lineJoin: result.pathData.lineJoin,
        dash: result.pathData.dash,
        opacity: result.pathData.opacity,
        visualData: result.pathData.visualData,
        isThickToThin: result.pathData.isThickToThin,
        isThinToThick: result.pathData.isThinToThick,
        segmentCount: result.pathData.segmentCount
      })
    }

    if (result.konvaObjects?.length > 0) {
      const layer = stage.findOne(`#layer-${activeLayer.id}`) as KonvaLayer
      if (layer) {
        result.konvaObjects.forEach((obj: KonvaLine | Konva.Group) => {
          if (obj.className === 'Line' && typeof (obj as KonvaLine).points === 'function') {
            const lineObj = obj as KonvaLine
            const validSmoothed = smoothedPoints.filter((p, idx) => {
              if (idx % 2 === 0) {
                return isFinite(p) && isFinite(smoothedPoints[idx + 1])
              }
              return true
            })
            
            if (validSmoothed.length >= 4) {
              lineObj.points(validSmoothed)
              lineObj.lineJoin('round')
              lineObj.lineCap('round')
              lineObj.tension(0)
              lineObj.closed(false)
            } else {
              lineObj.points(currentPath.points)
            }
          }

          if (!obj.getParent()) {
            layer.add(obj)
          }
        })

        if (result.originalPath) {
          result.originalPath.points(currentPath.points)
          if (!result.originalPath.getParent()) {
            layer.add(result.originalPath)
          }
        }
        
        stage.batchDraw()
      }
    }
  }

  const processBrushAsync = async (currentPath: DrawingPath, activeLayer: { id: string }, stage: KonvaStage): Promise<void> => {
    const smoothingMode = toolStore.currentTool.smoothingMode || 'curved'

    const smoothedPoints = smoothingMode !== 'curved' 
      ? await calculateSmoothedPoints(currentPath.points, smoothingMode)
      : currentPath.points
    
    currentPath.smoothedPoints = smoothedPoints
    currentPath.smoothingMode = smoothingMode

    const brushType = toolStore.currentTool.brushType || 'round'

    const needsWorkerProcessing = ['bow-tie', 'pointed', 'thick-to-thin', 'thin-to-thick'].includes(brushType)
    
    if (!needsWorkerProcessing) {
      return await finishBrushDrawing(currentPath, activeLayer, stage)
    }

    if (smoothingMode === 'straight') {
      return await finishBrushDrawing(currentPath, activeLayer, stage)
    }

    try {
      const cleanPoints = Array.isArray(smoothedPoints) 
        ? smoothedPoints.filter(p => typeof p === 'number' && isFinite(p))
        : []
      
      if (cleanPoints.length < 4) {
        return await finishBrushDrawing(currentPath, activeLayer, stage)
      }

      const result = await webWorkers.calculateTaperedLine({
        points: cleanPoints,
        originalStrokeWidth: Number(currentPath.strokeWidth) || 1,
        stroke: String(currentPath.stroke) || '#000000',
        opacity: Number(currentPath.opacity) || 1,
        type: brushType as 'pointed' | 'bow-tie' | 'thick-to-thin' | 'thin-to-thick',
        returnVisualData: true,
        segmentCount: Number(currentPath.segmentCount) || 0,
        smoothingMode: smoothingMode
      })

      if (result.visualSegments) {
        currentPath.visualData = { segments: result.visualSegments, dots: [] }
        currentPath.segmentCount = result.segmentCount
        currentPath.isTapered = true

        if (brushType === 'bow-tie') {
          currentPath.isThickToThin = false
          currentPath.isThinToThick = false
        } else if (brushType === 'thick-to-thin') {
          currentPath.isThickToThin = true
          currentPath.isThinToThick = false
        } else if (brushType === 'thin-to-thick') {
          currentPath.isThickToThin = false
          currentPath.isThinToThick = true
        }
      }

      await replaceSimpleLineWithBrushEffect(currentPath, activeLayer, stage, result.visualSegments || [])
    } catch (error) {
      await finishBrushDrawing(currentPath, activeLayer, stage)
    }
  }

  const endDrawing = async (stage: KonvaStage): Promise<void> => {
    if (isEndingDrawing.value) return
    const currentPath = canvasStore.currentPath
    const activeLayer = layerStore.currentLayer

    if (!currentPath || !activeLayer) {
      return
    }

    isEndingDrawing.value = true
    try {
      const brushType = toolStore.currentTool.brushType || 'round'
      const needsAsyncProcessing = ['bow-tie', 'pointed', 'thick-to-thin', 'thin-to-thick'].includes(brushType)

      if (needsAsyncProcessing) {
        await processBrushAsync(currentPath, activeLayer, stage)
        savePathToLayer(currentPath)
        history.addDrawingEntry(`Drew with ${brushType} brush`)

        emitDrawingCompleted({
          path: currentPath,
          layerId: activeLayer.id
        })

        canvasStore.setCurrentPath(null)
        canvasStore.setDrawing(false)
      } else {
        await finishBrushDrawing(currentPath, activeLayer, stage)

        savePathToLayer(currentPath)

        history.addDrawingEntry(`Drew with ${brushType} brush`)

        emitDrawingCompleted({
          path: currentPath,
          layerId: activeLayer.id
        })

        canvasStore.setCurrentPath(null)
        canvasStore.setDrawing(false)
      }
    } finally {
      isEndingDrawing.value = false
    }
  }

  const addPathToLayer = (path: DrawingPath, stage: KonvaStage): void => {
    const layer = stage.findOne(`#layer-${path.layerId}`) as KonvaLayer
    if (!layer || !path.points || path.points.length < 4) return

    const brushType = path.brushType || 'round'
    const brush = brushRegistry.getBrushByType(brushType)

    if (brush) {
      const context = {
        pathData: createPathData(path),
        konvaLayer: layer
      }
      brush.recreateFromData(context)
    } else {
      const line = new Konva.Line({
        points: path.points,
        stroke: path.stroke || '#000000',
        strokeWidth: path.strokeWidth || 1,
        opacity: path.opacity || 1,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false
      })
      layer.add(line)
    }

    stage.batchDraw()
  }

  const savePathToLayer = (path: DrawingPath): void => {
    const layer = layerStore.layers.find(l => l.id === path.layerId)
    if (layer) {
      if (!layer.paths) {
        layer.paths = []
      }
      layer.paths.push(path)
    }
  }

  const updatePathInLayer = (path: DrawingPath, _stage: KonvaStage): void => {
    const layer = _stage.findOne(`#layer-${path.layerId}`) as KonvaLayer
    if (!layer) return

    const line = layer.children.find((child: KonvaNode) =>
      child.getAttr('id') === path.id
    ) as KonvaLine

    if (line && path.points) {
      line.points(path.points)
      _stage.batchDraw()
    }
  }

  return {
    startDrawing,
    continueDrawing,
    endDrawing,
    getCanvasPosition,
    addPathToLayer,
    savePathToLayer,
    updatePathInLayer
  }
}